import { Link } from 'react-router'
import { PublicArticleMarkdown } from '#app/components/public-article-markdown.tsx'
import { SmoothScroller } from '#app/components/smooth-scroller.tsx'
import { type ChatMessagePayload } from '#app/services/ai/types.ts'
import { formatDateLong } from '#app/utils/date.js'
import { MarketingButton } from './marketing-button.tsx'

export function PublicArticleLayout(props: {
	title: string
	description?: string | null
	body?: string
	sourcePayloads?: ChatMessagePayload | null
	publishedAt?: Date | string | null
	reviewDate: Date | string
	children?: React.ReactNode
}) {
	return (
		<div className="theme-catpuccin min-h-[100dvh] bg-app-background text-app-foreground">
			<SmoothScroller />
			<article>
				<header className="relative min-h-[560px] overflow-hidden border-b border-app-border bg-[#fefefe] pt-24">
					<ArticleHeaderBackground />
					<div className="relative z-10 mx-auto max-w-6xl px-5 pb-14 pt-10 lg:px-8 lg:pb-20 lg:pt-14">
						<nav className="flex items-center gap-2 text-sm text-muted-foreground" aria-label="Breadcrumb">
							<Link to="/" className="font-medium text-foreground transition-colors hover:text-accent">
								IUS PRO
							</Link>
							<span className="text-app-border">/</span>
							<Link to="/articulos" className="transition-colors hover:text-accent">
								Artículos jurídicos
							</Link>
						</nav>

						<div className="mt-9 grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-end">
							<div className="max-w-4xl">
								<div className="flex flex-wrap gap-2">
									<span className="inline-flex items-center rounded-full border border-neutral-200 bg-white/85 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-neutral-700 shadow-sm backdrop-blur">
										Costa Rica
									</span>
								</div>
								<h1 className="mt-6 max-w-4xl text-balance text-4xl font-light leading-[1.04] tracking-tighter text-neutral-950 sm:text-5xl lg:text-6xl">
									{props.title}
								</h1>
								{props.description ? (
									<p className="mt-6 max-w-3xl text-pretty text-lg leading-10 text-neutral-500 sm:text-xl">
										{props.description}
									</p>
								) : null}
							</div>

							<aside className="border border-neutral-200 bg-white/85 p-5 shadow-[0_18px_50px_rgba(23,23,23,0.08)] backdrop-blur">
								<p className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500">
									Información del artículo
								</p>
								<dl className="mt-5 space-y-4 text-sm">
									<div className="flex items-start justify-between gap-4 border-b border-neutral-200 pb-4">
										<dt className="text-neutral-500">Publicado</dt>
										<dd className="text-right font-medium text-neutral-950">
											{props.publishedAt ? formatDateLong(props.publishedAt) : 'Sin fecha'}
										</dd>
									</div>
									<div className="flex items-start justify-between gap-4 border-b border-neutral-200 pb-4">
										<dt className="text-neutral-500">Revisado</dt>
										<dd className="text-right font-medium text-neutral-950">{formatDateLong(props.reviewDate)}</dd>
									</div>
									<div className="flex items-start justify-between gap-4">
										<dt className="text-neutral-500">Fuente</dt>
										<dd className="text-right font-medium text-neutral-950">Auri + IUS PRO</dd>
									</div>
								</dl>
							</aside>
						</div>
					</div>
				</header>

				<div className="mx-auto max-w-6xl px-5 pb-24 pt-12 lg:px-8 lg:pt-14">
					{props.body ? (
						<PublicArticleMarkdown
							body={props.body}
							sourcePayloads={props.sourcePayloads ?? null}
							className="max-w-3xl"
						/>
					) : (
						props.children
					)}
					<ArticleCTA />
				</div>
			</article>
		</div>
	)
}

export function ArticleCTA() {
	return (
		<section className="mt-20 max-w-4xl overflow-hidden border border-neutral-200 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
			<div className="relative p-6 sm:p-8 lg:p-10">
				<div
					className="pointer-events-none absolute inset-0 opacity-[0.1]"
					style={{
						backgroundImage:
							'linear-gradient(#d5d0c8 1px, transparent 1px), linear-gradient(90deg, #d5d0c8 1px, transparent 1px)',
						backgroundSize: '56px 56px',
					}}
				/>
				<div className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
					<div>
						<p className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500">IUS PRO</p>
						<h2 className="mt-4 max-w-2xl text-pretty text-3xl font-light leading-tight tracking-tight text-neutral-950 sm:text-4xl">
							Más velocidad para su equipo, con la precisión jurídica que exige la práctica legal.
						</h2>
						<p className="mt-5 max-w-2xl text-pretty text-base leading-7 text-neutral-600">
							Investigue, analice expedientes y redacte documentos con una IA especializada en derecho costarricense,
							diseñada para abogados, firmas y equipos legales que necesitan respuestas fundamentadas y verificables.
						</p>
					</div>
					<div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
						<MarketingButton asChild size="default" className="rounded-none px-6">
							<Link prefetch="render" to="/signup">
								Probar IUS PRO
							</Link>
						</MarketingButton>
						<MarketingButton asChild variant="secondary" size="default" className="rounded-none px-6">
							<Link to="/links/demo" target="_blank">
								Agendar demo
							</Link>
						</MarketingButton>
					</div>
				</div>
			</div>
		</section>
	)
}

function ArticleHeaderBackground() {
	return (
		<div className="pointer-events-none absolute inset-0" aria-hidden="true">
			<div
				className="absolute inset-0 opacity-[0.14]"
				style={{
					backgroundImage:
						'linear-gradient(#d5d0c8 1px, transparent 1px), linear-gradient(90deg, #d5d0c8 1px, transparent 1px)',
					backgroundSize: '80px 80px',
				}}
			/>
			<div className="absolute inset-0 overflow-hidden">
				<svg
					viewBox="0 0 1400 760"
					className="absolute -left-[12%] -top-[22%] h-[140%] w-[135%]"
					preserveAspectRatio="xMidYMid slice"
				>
					<defs>
						<linearGradient id="article-header-wave" x1="0%" y1="100%" x2="100%" y2="0%">
							<stop offset="0%" stopColor="#1a2744" />
							<stop offset="34%" stopColor="#2d3f5f" />
							<stop offset="58%" stopColor="#c9a962" />
							<stop offset="78%" stopColor="#e8d5a3" />
							<stop offset="100%" stopColor="#0f1829" />
						</linearGradient>
						<filter id="article-header-soft-glow" x="-20%" y="-20%" width="140%" height="140%">
							<feGaussianBlur stdDeviation="18" result="blur" />
							<feMerge>
								<feMergeNode in="blur" />
								<feMergeNode in="SourceGraphic" />
							</feMerge>
						</filter>
					</defs>
					<path
						d="M-140 610 C170 520 250 260 560 250 C880 238 960 430 1240 330 C1400 272 1510 170 1600 90"
						fill="none"
						stroke="url(#article-header-wave)"
						strokeWidth="118"
						strokeLinecap="round"
						opacity="0.12"
						filter="url(#article-header-soft-glow)"
					/>
					<path
						d="M-100 640 C220 700 330 360 640 330 C930 302 1030 520 1330 455 C1450 430 1540 360 1640 260"
						fill="none"
						stroke="#1a2744"
						strokeWidth="8"
						strokeLinecap="round"
						opacity="0.18"
					/>
					<path
						d="M-60 545 C230 610 390 430 650 405 C920 380 1040 500 1330 398"
						fill="none"
						stroke="#c9a962"
						strokeWidth="4"
						strokeLinecap="round"
						opacity="0.46"
					/>
				</svg>
			</div>
			<div className="absolute inset-0 bg-gradient-to-b from-white/20 via-white/70 to-[#fefefe]" />
			<div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-app-background to-transparent" />
		</div>
	)
}
